import { xget, xhttp } from "../Fetchv2.js";
import { PGSchema } from "../schemas/pg.schema.js";

export class Payroll {
	// ─── Payroll Concepts ─────────────────────────────────────────────────────

	async getConcepts(pgid: PGSchema.pages["id"], params?: { country_id?: number; type?: string; system?: boolean }, token?: string) {
		return xget<{ items: PGSchema.hcm_payroll_concepts[] }>(`/pages/${pgid}/payroll-concepts`, params, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async createConcept(pgid: PGSchema.pages["id"], data: Omit<PGSchema.hcm_payroll_concepts, "id" | "created_at" | "updated_at">, token?: string) {
		return xhttp<PGSchema.hcm_payroll_concepts>(`/pages/${pgid}/payroll-concepts`, "POST", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async updateConcept(pgid: PGSchema.pages["id"], id: string, data: Partial<Omit<PGSchema.hcm_payroll_concepts, "id" | "created_at" | "updated_at">>, token?: string) {
		return xhttp<PGSchema.hcm_payroll_concepts>(`/pages/${pgid}/payroll-concepts/${id}`, "PUT", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async deleteConcept(pgid: PGSchema.pages["id"], id: string, token?: string) {
		return xhttp<{ deleted: boolean }>(
			`/pages/${pgid}/payroll-concepts/${id}`,
			"DELETE",
			{},
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}

	// ─── Agreement Items ──────────────────────────────────────────────────────

	async getAgreementItems(pgid: PGSchema.pages["id"], agreementId: string, token?: string) {
		return xget<{ items: PGSchema.hcm_agreement_items[] }>(`/pages/${pgid}/agreements/${agreementId}/items`, undefined, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async createAgreementItem(pgid: PGSchema.pages["id"], agreementId: string, data: { concept_id: string; override_value?: number | null; active?: boolean }, token?: string) {
		return xhttp<PGSchema.hcm_agreement_items>(`/pages/${pgid}/agreements/${agreementId}/items`, "POST", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async updateAgreementItem(pgid: PGSchema.pages["id"], agreementId: string, id: string, data: { override_value?: number | null; active?: boolean }, token?: string) {
		return xhttp<PGSchema.hcm_agreement_items>(`/pages/${pgid}/agreements/${agreementId}/items/${id}`, "PUT", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async deleteAgreementItem(pgid: PGSchema.pages["id"], agreementId: string, id: string, token?: string) {
		return xhttp<{ deleted: boolean }>(
			`/pages/${pgid}/agreements/${agreementId}/items/${id}`,
			"DELETE",
			{},
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}

	// ─── Payroll adjustments (one-off) ─────────────────────────────────────

	async getPayrollAdjustments(pgid: PGSchema.pages["id"], params?: { agreement_id?: string; run_id?: string }, token?: string) {
		return xget<{ items: PGSchema.hcm_payroll_adjustments[] }>(`/pages/${pgid}/payroll-adjustments`, params, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async createPayrollAdjustment(pgid: PGSchema.pages["id"], data: { agreement_id: string; concept_id: string; amount: number; notes?: string | null; run_id?: string | null }, token?: string) {
		return xhttp<PGSchema.hcm_payroll_adjustments>(`/pages/${pgid}/payroll-adjustments`, "POST", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async updatePayrollAdjustment(pgid: PGSchema.pages["id"], id: string, data: { amount?: number; notes?: string | null; run_id?: string | null }, token?: string) {
		return xhttp<PGSchema.hcm_payroll_adjustments>(`/pages/${pgid}/payroll-adjustments/${id}`, "PUT", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async deletePayrollAdjustment(pgid: PGSchema.pages["id"], id: string, token?: string) {
		return xhttp<{ deleted: boolean }>(
			`/pages/${pgid}/payroll-adjustments/${id}`,
			"DELETE",
			{},
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}

	// ─── Payroll Runs ─────────────────────────────────────────────────────────

	async getPayrollRuns(pgid: PGSchema.pages["id"], params?: { entity_id?: string; status?: string }, token?: string) {
		return xget<{ items: PGSchema.hcm_payroll_runs[] }>(`/pages/${pgid}/payroll-runs`, params, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async createPayrollRun(pgid: PGSchema.pages["id"], data: { name: string; period_start: string; period_end: string; entity_id?: string | null }, token?: string) {
		return xhttp<{ run: PGSchema.hcm_payroll_runs }>(`/pages/${pgid}/payroll-runs`, "POST", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async getPayrollRun(pgid: PGSchema.pages["id"], id: string, token?: string) {
		return xget<PGSchema.hcm_payroll_runs>(`/pages/${pgid}/payroll-runs/${id}`, undefined, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async updatePayrollRun(
		pgid: PGSchema.pages["id"],
		id: string,
		data: Partial<{ name: string; period_start: string; period_end: string; entity_id: string | null; status: string }>,
		token?: string
	) {
		return xhttp<PGSchema.hcm_payroll_runs>(`/pages/${pgid}/payroll-runs/${id}`, "PUT", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async deletePayrollRun(pgid: PGSchema.pages["id"], id: string, token?: string) {
		return xhttp<{ deleted: boolean }>(
			`/pages/${pgid}/payroll-runs/${id}`,
			"DELETE",
			{},
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}

	async executePayrollRun(pgid: PGSchema.pages["id"], id: string, token?: string) {
		return xhttp<{ run: PGSchema.hcm_payroll_runs }>(
			`/pages/${pgid}/payroll-runs/${id}/execute`,
			"POST",
			{},
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}

	async approvePayrollRun(pgid: PGSchema.pages["id"], id: string, status: string, token?: string) {
		return xhttp<PGSchema.hcm_payroll_runs>(
			`/pages/${pgid}/payroll-runs/${id}/approve`,
			"PUT",
			{ status },
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}

	// ─── Payslips ─────────────────────────────────────────────────────────────

	async getPayslip(pgid: PGSchema.pages["id"], id: string, token?: string) {
		return xget<PGSchema.hcm_payslips>(`/pages/${pgid}/payslips/${id}`, undefined, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	// ─── Simulation ───────────────────────────────────────────────────────────

	async simulate(
		pgid: PGSchema.pages["id"],
		data: {
			agreement_id?: string;
			salary_cents?: number;
			country_id?: number;
			run_id?: string;
			custom_concepts?: Partial<PGSchema.hcm_payroll_concepts>[];
			pay_period?: string | null;
			sv_recalculo?: {
				tipo: "JUNIO_SEMESTRAL" | "DICIEMBRE_ANUAL";
				salario_gravado_acumulado_cents: number;
				renta_ya_retenida_cents: number;
			};
			/** GT: acumulados antes del mes simulado; el backend suma el gravable del mes actual */
			gt_isr_ytd?: {
				taxable_gross_acumulado_cents: number;
				isr_retenido_acumulado_cents: number;
				calendar_month: number;
			};
		},
		token?: string
	) {
		return xhttp<{
			total_base_cents: number;
			total_earnings_cents: number;
			total_deductions_cents: number;
			net_pay_cents: number;
			total_employer_cost_cents: number;
			items: PGSchema.hcm_payslip_items[];
		}>(`/pages/${pgid}/payroll-simulate`, "POST", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	// ─── Saldos iniciales ISR (SV) ────────────────────────────────────────────

	async getPayrollOpeningBalances(pgid: PGSchema.pages["id"], params?: { agreement_id?: string }, token?: string) {
		return xget<{ items: PGSchema.hcm_payroll_opening_balances[] }>(`/pages/${pgid}/payroll-opening-balances`, params, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async putPayrollOpeningBalance(
		pgid: PGSchema.pages["id"],
		data: {
			agreement_id: string;
			tax_year: number;
			salario_gravado_acumulado_cents?: number;
			renta_retenida_acumulado_cents?: number;
			notes?: string | null;
		},
		token?: string
	) {
		return xhttp<PGSchema.hcm_payroll_opening_balances>(`/pages/${pgid}/payroll-opening-balances`, "PUT", data, {
			headers: token ? { "app-token": token } : undefined
		});
	}

	async deletePayrollOpeningBalance(pgid: PGSchema.pages["id"], id: string, token?: string) {
		return xhttp<{ deleted: boolean }>(
			`/pages/${pgid}/payroll-opening-balances/${id}`,
			"DELETE",
			{},
			{
				headers: token ? { "app-token": token } : undefined
			}
		);
	}
}
